// StatusPill — shared status indicator: a navy label beside a colored dot.
//
// Why a dot, not colored text: success-green / amber / red text on white
// fails WCAG AA at small sizes (the old green-on-green "ACTIVE" pill was a
// real contrast failure). Here the COLOR lives in the dot and the LABEL
// stays navy, so a record is always readable from its text — color is a
// redundant cue, never the only one.
//
// Reads semantic tokens only, so it works in light + dark mode. Factored
// out of the Service Records page as the OS-wide pattern (the brief's
// StatusPill primitive) — any list or detail surface can render it.
//
// THIS IS THE ONE STATUS PRIMITIVE. New status-bearing entities add a
// `status → { tone, label }` mapper below and render <StatusPill {...} />;
// never hand-roll colored status text. Convention + rationale:
// docs/design/list-views-and-status-pills.md

import type { ReactNode } from "react";

import {
  sentenceCase,
  serviceRecordStatus,
  serviceRecordStatusWithDisposition,
  serviceRecordStatusWithWorkflow,
  type StatusTone,
} from "./status-pill-helpers";

// Pure mappers live in status-pill-helpers.ts (node-pool testable, no
// JSX); re-exported here so call sites keep one import path.
export type { StatusTone };
export {
  serviceRecordStatus,
  serviceRecordStatusWithDisposition,
  serviceRecordStatusWithWorkflow,
};

export function StatusPill({
  tone,
  label,
  title,
}: {
  tone: StatusTone;
  label: ReactNode;
  /** Optional hover tooltip (e.g. "Running — touchpoints fire on schedule"). */
  title?: string;
}) {
  return (
    <span className="status-pill" {...(title ? { title } : {})}>
      <span
        className={`status-pill__dot status-pill__dot--${tone}`}
        aria-hidden="true"
      />
      {label}
    </span>
  );
}

// (Service-record status mappers moved to status-pill-helpers.ts —
// re-exported above.)

// Ongoing Care cycle status → tone + label. Active is green; paused needs
// a glance (amber); terminal states are neutral.
const RHYTHM_STATUS: Record<string, { tone: StatusTone; label: string }> = {
  active: { tone: "active", label: "Active" },
  paused: { tone: "review", label: "Paused" },
  completed: { tone: "neutral", label: "Completed" },
  cancelled: { tone: "neutral", label: "Cancelled" },
  ended: { tone: "neutral", label: "Ended" },
};

export function rhythmStatus(status: string): {
  tone: StatusTone;
  label: string;
} {
  return (
    RHYTHM_STATUS[status] ?? { tone: "neutral", label: sentenceCase(status) }
  );
}

// Ongoing Care PLAN status collapses the orthogonal `status` +
// `is_live` columns into the one word an advisor needs: is it running?
//   active + live      → Running       (touchpoints fire on schedule)
//   active + not live  → Needs review  (exists, not started yet)
//   paused/completed/cancelled → as labeled
export function planStatusLabel(status: string, isLive: number): string {
  if (status === "active") return isLive ? "Running" : "Needs review";
  if (status === "paused") return "Paused";
  if (status === "completed") return "Completed";
  if (status === "cancelled") return "Cancelled";
  return sentenceCase(status);
}

export function planStatus(
  status: string,
  isLive: number,
): { tone: StatusTone; label: string } {
  const label = planStatusLabel(status, isLive);
  if (label === "Running") return { tone: "active", label };
  if (label === "Needs review") return { tone: "review", label };
  return { tone: "neutral", label };
}

// Automation state → tone + label. Drives the rule's status pill on the
// Automations workspace. Active is the only "good" green; paused is amber
// (a glance — it exists but isn't firing); draft + every terminal/setup
// state is neutral gray; error is the hard red. Maps the engine's 8-state
// union; the redesigned list filters to Active/Draft/Paused but "All" shows
// every state, so unmapped-looking values still render with a real tone.
const AUTOMATION_STATE: Record<string, { tone: StatusTone; label: string }> = {
  active: { tone: "active", label: "Active" },
  paused: { tone: "review", label: "Paused" },
  draft: { tone: "neutral", label: "Draft" },
  awaiting_review: { tone: "review", label: "Awaiting review" },
  test: { tone: "neutral", label: "Test" },
  error: { tone: "stalled", label: "Error" },
  rolled_back: { tone: "neutral", label: "Rolled back" },
  archived: { tone: "neutral", label: "Archived" },
};

export function automationState(state: string): {
  tone: StatusTone;
  label: string;
} {
  return (
    AUTOMATION_STATE[state] ?? { tone: "neutral", label: sentenceCase(state) }
  );
}

// Opportunity outcome (won/lost/abandoned) → tone + label. Won is the
// "good" green; lost reads as the hard stop (red); abandoned is neutral.
export function opportunityOutcome(outcome: string): {
  tone: StatusTone;
  label: string;
} {
  if (outcome === "won") return { tone: "active", label: "Won" };
  if (outcome === "lost") return { tone: "stalled", label: "Lost" };
  if (outcome === "abandoned") return { tone: "neutral", label: "Abandoned" };
  return { tone: "neutral", label: sentenceCase(outcome) };
}

// Transaction (Processing) outcome (completed/fell_through/cancelled).
export function transactionOutcome(outcome: string): {
  tone: StatusTone;
  label: string;
} {
  if (outcome === "completed") return { tone: "active", label: "Completed" };
  if (outcome === "fell_through")
    return { tone: "stalled", label: "Fell through" };
  if (outcome === "cancelled") return { tone: "neutral", label: "Cancelled" };
  return { tone: "neutral", label: sentenceCase(outcome) };
}
